import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInbox } from "@fortawesome/sharp-thin-svg-icons"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import {
  Box,
  Button,
  Card,
  Menu,
  MenuItem,
  Stack,
  styled,
  Typography,
} from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { gridClasses } from "@mui/x-data-grid-pro"
import Papa from "papaparse"
import type React from "react"
import { useCallback, useMemo, useState } from "react"
import { downloadFile } from "../../utils/downloadFile"
import { extraColors } from "../../utils/theme"
import { BaseGrid } from "../BaseGrid/BaseGrid"
import { useAlertCuration, type BiothreatSignal } from "./AlertCurationContext"
import { getRowClassName, sharedGridSx } from "./AlertCurationShared"
import { getPotentialAlertColumns } from "./AlertCurationTableColumns/PotentialAlertsColumns"
import { DEFAULT_SCORE, getExportFileName } from "./AlertCurationUtils"

const SectionTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h3Bold,
  color: extraColors.white,
  letterSpacing: "-0.01em",
}))

const EmptyStateContainer = styled(Stack)(() => ({
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  color: extraColors.hint,
  textAlign: "center",
  padding: "60px",
  background: `radial-gradient(circle at center, ${extraColors.translucent5} 0%, transparent 70%)`,
}))

const PotentialAlertsCard = styled(Card)(() => ({
  minHeight: 100,
  height: 280,
  bgcolor: extraColors.translucent5,
  border: `1px solid ${extraColors.translucent8}`,
  borderRadius: "12px",
  display: "flex",
  flexDirection: "column",
}))

const CountBadge = styled(Box)(() => ({
  bgcolor: extraColors.translucent8,
  color: extraColors.status.navyLight,
  px: 1,
  py: 0.2,
  borderRadius: "10px",
  fontSize: "0.65rem",
  fontWeight: 900,
}))

const ExportButton = styled(Button)(() => ({
  height: 32,
  bgcolor: extraColors.status.navyLight,
  color: extraColors.dark,
  fontWeight: 800,
  fontSize: "0.75rem",
  px: 2,
  borderRadius: "6px",
  textTransform: "none",
  "&:hover": {
    bgcolor: extraColors.status.navy,
  },
}))

const EmptyStateText = styled(Typography)(() => ({
  opacity: 0.3,
  fontWeight: 800,
  fontSize: "0.65rem",
  letterSpacing: "0.05em",
}))

interface PotentialAlertsOptionsMenuProps {
  anchorEl: HTMLElement | null
  open: boolean
  onMenuClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  onMenuClose: () => void
  onExportCsv: () => void
  disabled: boolean
}

export const PotentialAlerts: React.FC = () => {
  const { pinnedSignals, removeFromPinnedSignals: removeFromPotential } =
    useAlertCuration()

  const potentialColumns = useMemo(
    () =>
      getPotentialAlertColumns({
        removeFromPotential,
      }),
    [removeFromPotential]
  )

  return (
    <Box sx={{ flexShrink: 0 }}>
      <PotentialAlertsHeader />

      <PotentialAlertsCard>
        {pinnedSignals.size === 0 ? (
          <EmptyStateGrid />
        ) : (
          <PotentialAlertsGrid columns={potentialColumns} />
        )}
      </PotentialAlertsCard>
    </Box>
  )
}

const PotentialAlertsHeader: React.FC = () => {
  const { pinnedSignals } = useAlertCuration()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openMenu = Boolean(anchorEl)

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleExportCsv = useCallback(() => {
    exportPotentialAlertsToCsv(Array.from(pinnedSignals.values()))
    handleMenuClose()
  }, [pinnedSignals])

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <PotentialAlertsTitle />
      <PotentialAlertsOptionsMenu
        anchorEl={anchorEl}
        open={openMenu}
        onMenuClick={handleMenuClick}
        onMenuClose={handleMenuClose}
        onExportCsv={handleExportCsv}
        disabled={pinnedSignals.size === 0}
      />
    </Stack>
  )
}

const PotentialAlertsTitle: React.FC = () => {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        alignItems: "center",
      }}
    >
      <SectionTitle sx={{ fontSize: "1rem" }}>Selected Signals</SectionTitle>
      <PotentialAlertsCount />
    </Stack>
  )
}

const PotentialAlertsCount: React.FC = () => {
  const { pinnedSignals } = useAlertCuration()
  return <CountBadge>{pinnedSignals.size}</CountBadge>
}

const PotentialAlertsOptionsMenu: React.FC<PotentialAlertsOptionsMenuProps> = ({
  anchorEl,
  open,
  onMenuClick,
  onMenuClose,
  onExportCsv,
  disabled,
}) => {
  return (
    <Box>
      <ExportButton
        variant="contained"
        startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
        endIcon={<ArrowDropDownIcon />}
        onClick={onMenuClick}
        disabled={disabled}
      >
        Export
      </ExportButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onMenuClose}
        slotProps={{
          paper: {
            sx: {
              bgcolor: extraColors.dark,
              border: `1px solid ${extraColors.translucent8}`,
              mt: 1,
              "& .MuiMenuItem-root": {
                fontSize: "0.75rem",
                fontWeight: 700,
                color: extraColors.white,
                "&:hover": {
                  bgcolor: extraColors.translucent8,
                },
              },
            },
          },
        }}
      >
        <MenuItem onClick={onExportCsv}>CSV</MenuItem>
        <MenuItem onClick={onMenuClose} disabled={true}>
          PDF
        </MenuItem>
        <MenuItem onClick={onMenuClose} disabled={true}>
          Publish
        </MenuItem>
        <MenuItem onClick={onMenuClose} disabled={true}>
          Email
        </MenuItem>
      </Menu>
    </Box>
  )
}

const PotentialAlertsGrid: React.FC<{
  columns: GridColDef<BiothreatSignal>[]
}> = ({ columns }) => {
  const { pinnedSignals } = useAlertCuration()
  return (
    <BaseGrid
      rows={Array.from(pinnedSignals.values())}
      columns={columns}
      density="compact"
      checkbox={false}
      search={false}
      filter={false}
      sort={true}
      initSortModel={[{ field: "modern_score", sort: "desc" }]}
      sortingMode="client"
      pagination={false}
      getRowHeight={() => 58}
      getRowClassName={getRowClassName}
      sx={{
        ...sharedGridSx,
        minHeight: "unset",
        height: "100%",
        [`& .${gridClasses.footerContainer}`]: {
          display: "none",
        },
      }}
    />
  )
}

const EmptyStateGrid: React.FC = () => {
  return (
    <EmptyStateContainer spacing={1.5} sx={{ p: 4 }}>
      <FontAwesomeIcon icon={faInbox} style={{ fontSize: 60, opacity: 0.5 }} />
      <EmptyStateText variant="body2">
        SELECT CANDIDATES TO POPULATE
      </EmptyStateText>
    </EmptyStateContainer>
  )
}

const exportPotentialAlertsToCsv = (signals: BiothreatSignal[]) => {
  const dataToExport = signals.map(a => {
    const clusterCount = a.cluster_count || a.cluster_count || 1

    return {
      location_name: a.resolved_location || "",
      disease_codes: JSON.stringify(a.clean_disease_codes),
      alert: `${(a.modern_score ?? DEFAULT_SCORE * 100).toFixed(2)}%`,
      "legacy alert": `${(a.legacy_score ?? DEFAULT_SCORE * 100).toFixed(2)}%`,
      page_title: a.title,
      url: a.url,
      cluster_size: clusterCount,
      threat: a.threat_category || "Infectious Disease",
    }
  })

  const csv = Papa.unparse(dataToExport)
  downloadFile(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    getExportFileName()
  )
}
